import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

import type { RootState } from '../store'
import { Screen } from '../../types/global'
import { getService } from '../../helpers/ReduxHelpers'
import { ApiResponse } from '../api/types/state'
import { services } from '../../graphql'
import {
  AppUnregisterDataQueryVariables,
  AppUnregisterMutationVariables,
  PublicExpoAuthenticateVisitorMutationVariables,
  PublicExpoCurrentEventQueryVariables,
  PublicExpoEventBySlugQueryVariables,
  PublicExpoRegisterMutationVariables,
  PublicExpoSendVerificationCodeMutationVariables,
  PublicExpoUpdateRegistrationMutationVariables,
} from '../../graphql/types/api'
import { RegisterFormValues } from '../../components/forms/RegisterForm'

import { actionTypes } from './types'
//
// Services
//

const getEventBySlugService = getService<
  'getEventBySlug',
  ApiResponse<typeof services.app.queries.eventBySlug>['data'],
  PublicExpoEventBySlugQueryVariables
>('getEventBySlug')

const getCurrentEventService = getService<
  'getCurrentEvent',
  ApiResponse<typeof services.app.queries.currentEvent>['data'],
  PublicExpoCurrentEventQueryVariables
>('getCurrentEvent')

const getLabelsService = getService<
  'getLabels',
  ApiResponse<typeof services.app.queries.labels>['data']
>('getLabels')

const sendVerificationCodesService = getService<
  'sendVerificationCodes',
  ApiResponse<typeof services.app.mutations.sendVerificationCodes>['data'],
  PublicExpoSendVerificationCodeMutationVariables
>('sendVerificationCodes')

const authenticateVisitorService = getService<
  'authenticateVisitor',
  ApiResponse<typeof services.app.mutations.authenticateVisitor>['data'],
  PublicExpoAuthenticateVisitorMutationVariables
>('authenticateVisitor')

const registerService = getService<
  'register',
  ApiResponse<typeof services.app.mutations.register>['data'],
  PublicExpoRegisterMutationVariables
>('register')

const unregisterService = getService<
  'unregister',
  ApiResponse<typeof services.app.mutations.unregister>['data'],
  AppUnregisterMutationVariables
>('unregister')

const getUnregisterDataService = getService<
  'getUnregisterData',
  ApiResponse<typeof services.app.queries.unregisterData>['data'],
  AppUnregisterDataQueryVariables
>('getUnregisterData')

const updateRegistrationService = getService<
  'updateRegistration',
  ApiResponse<typeof services.app.mutations.updateRegistration>['data'],
  PublicExpoUpdateRegistrationMutationVariables
>('updateRegistration')

const deleteRegistrationService = getService<
  'deleteRegistration',
  ApiResponse<typeof services.app.mutations.deleteRegistration>['data'],
  null
>('deleteRegistration')

export type AppState = {
  isInit: boolean
  screen: Screen
  registerFormValues: RegisterFormValues | null
  currentLocale: string
  authenticationToken: string | null
  hasCanceledSlot: boolean
  hasUpdatedSlot: boolean
  selectedSlotId: string | null
  registerCode: string | null
  getEventBySlug: typeof getEventBySlugService.values
  getLabels: typeof getLabelsService.values
  sendVerificationCodes: typeof sendVerificationCodesService.values
  register: typeof registerService.values
  unregister: typeof unregisterService.values
  getUnregisterData: typeof getUnregisterDataService.values
  getCurrentEvent: typeof getCurrentEventService.values
  authenticateVisitor: typeof authenticateVisitorService.values
  updateRegistration?: typeof updateRegistrationService.values
  deleteRegistration?: typeof deleteRegistrationService.values
}

//
// Initial state
//

const initialState: AppState = {
  isInit: false,
  screen: Screen.register,
  registerFormValues: null,
  currentLocale: 'en',
  authenticationToken: null,
  selectedSlotId: null,
  registerCode: null,
  hasCanceledSlot: false,
  hasUpdatedSlot: false,
  getEventBySlug: getEventBySlugService.values,
  getLabels: getLabelsService.values,
  sendVerificationCodes: sendVerificationCodesService.values,
  register: registerService.values,
  unregister: unregisterService.values,
  getUnregisterData: getUnregisterDataService.values,
  getCurrentEvent: getCurrentEventService.values,
  authenticateVisitor: authenticateVisitorService.values,
  updateRegistration: updateRegistrationService.values,
  deleteRegistration: deleteRegistrationService.values,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'app',
  initialState,
  extraReducers: (builder) =>
    builder.addCase<any, PayloadAction<any>>(REHYDRATE, (_state, action) => {
      return {
        ...initialState,
        ...action.payload?.app,
        getEventBySlug: getEventBySlugService.values,
        register: registerService.values,
        getCurrentEvent: getCurrentEventService.values,
        registerCode: null,
        selectedSlotId: null,
        authenticateVisitor: authenticateVisitorService.values,
        updateRegistration: updateRegistrationService.values,
        deleteRegistration: deleteRegistrationService.values,
      }
    }),
  reducers: {
    init: (_state, _action: actionTypes.init) => undefined,
    reset: (state) => {
      state.getEventBySlug = getEventBySlugService.values
      state.register = registerService.values
      state.getCurrentEvent = getCurrentEventService.values
      state.registerCode = null
      state.selectedSlotId = null
      state.authenticateVisitor = authenticateVisitorService.values
      state.updateRegistration = updateRegistrationService.values
      state.deleteRegistration = deleteRegistrationService.values
    },
    setIsInit: (state, action: actionTypes.setIsInit) => {
      const { isInit } = action.payload
      state.isInit = isInit
    },
    setCurrentLocale: (state, action: actionTypes.setCurrentLocale) => {
      state.currentLocale = action.payload
    },
    switchLocale: (_state, _action: actionTypes.setCurrentLocale) => undefined,
    setScreen: (state, action: actionTypes.setScreen) => {
      state.screen = action.payload
    },
    setRegisterFormValues: (state, action: actionTypes.setRegisterFormValues) => {
      state.registerFormValues = action.payload
    },
    setSelectedSlotId: (state, action: actionTypes.setSelectedSlotId) => {
      state.selectedSlotId = action.payload
    },
    setRegisterCode: (state, action: actionTypes.setRegisterCode) => {
      state.registerCode = action.payload
    },
    setAuthenticationToken: (state, action: actionTypes.setAuthenticationToken) => {
      state.authenticationToken = action.payload
    },
    setHasUpdatedSlot: (state, action: actionTypes.setHasUpdatedSlot) => {
      state.hasUpdatedSlot = action.payload
    },
    setHasCanceledSlot: (state, action: actionTypes.setHasCanceledSlot) => {
      state.hasCanceledSlot = action.payload
    },
    ...getEventBySlugService.reducers,
    ...getLabelsService.reducers,
    ...sendVerificationCodesService.reducers,
    ...registerService.reducers,
    ...unregisterService.reducers,
    ...getUnregisterDataService.reducers,
    ...getCurrentEventService.reducers,
    ...authenticateVisitorService.reducers,
    ...updateRegistrationService.reducers,
    ...deleteRegistrationService.reducers,
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const isInit = (state: RootState) => root(state).isInit
const screen = (state: RootState) => root(state).screen
const currentLocale = (state: RootState) => root(state).currentLocale
const selectedSlotId = (state: RootState) => root(state).selectedSlotId
const authenticationToken = (state: RootState) => root(state).authenticationToken
const hasCanceledSlot = (state: RootState) => root(state).hasCanceledSlot
const hasUpdatedSlot = (state: RootState) => root(state).hasUpdatedSlot
const registerCode = (state: RootState) => root(state).registerCode
const registerFormValues = (state: RootState) => root(state).registerFormValues
const getEventBySlug = (state: RootState) => root(state).getEventBySlug
const getLabels = (state: RootState) => root(state).getLabels
const sendVerificationCodes = (state: RootState) => root(state).sendVerificationCodes
const register = (state: RootState) => root(state).register
const unregister = (state: RootState) => root(state).unregister
const getUnregisterData = (state: RootState) => root(state).getUnregisterData
const getCurrentEvent = (state: RootState) => root(state).getCurrentEvent
const authenticateVisitor = (state: RootState) => root(state).authenticateVisitor
const visitor = (state: RootState) => root(state).authenticateVisitor.data?.visitor
const updateRegistration = (state: RootState) => root(state).updateRegistration
const deleteRegistration = (state: RootState) => root(state).deleteRegistration

export const selectors = {
  isInit,
  screen,
  currentLocale,
  getEventBySlug,
  getLabels,
  registerFormValues,
  sendVerificationCodes,
  register,
  unregister,
  getUnregisterData,
  getCurrentEvent,
  selectedSlotId,
  registerCode,
  authenticationToken,
  hasCanceledSlot,
  hasUpdatedSlot,
  authenticateVisitor,
  visitor,
  updateRegistration,
  deleteRegistration,
}
